<template>
  <div>
    <AlertDismiss ref="alertDismiss" />

    <BaseModal ref="modal" v-model="showModal" size="xl">
      <div class="d-flex align-items-center justify-content-center">
        <div v-if="!route" class="text-center text-secondary my-2">
          <b-spinner class="align-middle me-2"></b-spinner>
          <strong>Obteniendo enlace del recurso...</strong>
        </div>

        <div v-else class="text-center">
          <DocumentPdf v-if="isPdf" :pdfUrl="route" />
          <img
            v-else
            :src="route"
            class="img-fluid rounded shadow mx-auto d-block"
            :alt="currentItem?.name"
          />
        </div>
      </div>
    </BaseModal>

    <b-table
      :items="items"
      :fields="fields"
      class="table-container"
      sticky-header="48vh"
      show-empty
    >
      <template #empty>
        <b-card class="text-center my-2 fs-5">
          Aún no se ha agregado ninguna evidencia
        </b-card>
      </template>
      <template #cell(options)="data">
        <div class="d-flex align-items-center">
          <button
            class="btn btn-sm py-0"
            @click="openDocumentation(data.item)"
            v-b-tooltip.hover.left
            title="Ver recurso"
          >
            <b-icon
              v-if="isPdfDoc(data.item)"
              icon="file-pdf"
              variant="primary"
              class="me-2"
            />
            <b-icon v-else icon="file-image" variant="primary" class="me-2" />
          </button>
        </div>
      </template>
      <template #cell(document)="data">
        <div class="d-flex align-items-center">
          <button
            class="btn btn-light btn-sm py-0"
            @click="openDocumentation(data.item)"
            v-b-tooltip.hover.left
            title="Ver recurso"
          >
            <b-icon icon="file-image-fill" variant="primary" class="me-2" />
          </button>
        </div>
      </template>
    </b-table>

    <form @submit.prevent="onSubmit" v-permission="'reuniones-evidencias-vista-crear'">
      <label>Nueva evidencia</label>
      <div class="mb-3">
        <input
          type="file"
          class="form-control"
          id="fileInput"
          ref="fileInput"
          @change="handleFileChange"
          required
          accept="image/*,.pdf"
        />
      </div>
      <button type="submit" class="btn btn-primary" :disabled="isUploading">
        <b-spinner small v-if="isUploading"></b-spinner>
        <b-icon small v-else icon="plus" />
        Añadir evidencia
      </button>
    </form>
  </div>
</template>

<script>
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import BaseModal from "@/components/shared/Base-modal.vue";
import DocumentPdf from "@/components/shared/Document-pdf.vue";
import { formatDateTime } from "@/plugins/complianceScripts";
import { mapActions } from "vuex";

export default {
  name: "MeetingDocumentation",

  props: {
    meetingId: String,
  },

  components: {
    BaseModal,
    AlertDismiss,
    DocumentPdf,
  },

  data() {
    return {
      isUploading: false,
      fields: [
        { key: "name", label: "Nombre" },
        { key: "creationDate", label: "Fecha de creación", formatter: formatDateTime },
        { key: 'options', label: '', thClass: 'col-1'}
      ],

      file: null,
      routeUpload: "",

      showModal: false,
      route: "",
      currentItem: null,
      items: [],
    };
  },

  computed: {
    isPdf() {
      return this.isPdfDoc(this.currentItem);
    },
  },

  methods: {
    ...mapActions([
      "getMeetingDocumentationUrl",
      "allMeetingDocumentation",
      "addMeetingDocumentation",
      "uploadMeetingDocumentation",
    ]),

    async getAllDocumentation() {
      let result = await this.allMeetingDocumentation({ meetId: this.meetingId });
      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message);
        return;
      }

      this.items = result.data;
    },

    async onSubmit() {
      this.isUploading = true;
      let result = await this.uploadMeetingDocumentation(this.file);
      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message);
        this.isUploading = false;
        return;
      }

      this.routeUpload = result.data;

      let resultAssign = await this.addMeetingDocumentation({
        meetingId: this.meetingId,
        route: this.routeUpload,
        name: this.file.name,
      });

      if (resultAssign.isError) {
        this.$refs.alertDismiss.showAlert(resultAssign.message);
        this.isUploading = false;
        return;
      }

      await this.getAllDocumentation();
      this.$refs.alertDismiss.showAlert("Evidencia creada correctamente", "success");
      this.$refs.fileInput.value = null;
      this.file = null;
      this.routeUpload = null;
      this.isUploading = false;
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.file = file;
      }
    },

    async openDocumentation(item) {
      this.showModal = true;
      this.currentItem = item;
      this.route = "";
      try {
        let result = await this.getMeetingDocumentationUrl({
          meetId: this.meetingId,
          route: item.route,
        });
        if (result.isError) {
          this.$refs.alertDismiss.showAlert(result.message);
          this.showModal = false;
          return;
        }

        this.route = result.data;
        this.showModal = true;
      } catch (error) {
        console.error("Error fetching documentation URL:", error);
        this.$refs.alertDismiss.showAlert(
          "Error al obtener la documentación",
          "danger"
        );
        this.showModal = false;
      }
    },

    isPdfDoc(data) {
      const fileName = data?.name?.toLowerCase() ?? "";
      return fileName.endsWith(".pdf");
    },
  },

  watch: {
    meetingId: {
      immediate: true,
      async handler(newVal) {
        this.items = [];
        if (newVal === null) {
          return;
        }
        await this.getAllDocumentation();
      },
    },
  },
};
</script>

